import { z } from "zod";
import type { TextFieldValidationType } from "@/commonTypes/question";

export type { TextFieldValidationType };

/**
 * Regex patterns for validation - strict allowlists only
 * No special characters that could be used for injection attacks
 */
const PATTERNS = {
  // Name: letters (including accented), spaces, hyphens, apostrophes only
  // Explicitly blocks: < > & " ' ` \ / = ; : ( ) { } [ ] | ! @ # $ % ^ * + ? ~
  // Split À-ÿ to exclude × (U+00D7) and ÷ (U+00F7)
  name: /^[a-zA-ZÀ-ÖØ-öø-ÿ\s'-]+$/,
  // Phone: digits, spaces, hyphens, dots, parentheses only
  phone: /^[0-9\s\-.()]+$/,
  // Number: digits only (integers)
  number: /^[0-9]+$/,
  // Decimal number: digits with up to 2 decimal places (e.g., "70.5", "155.25")
  // Limits to 2 digits after decimal point for health/weight measurements
  decimal: /^[0-9]+(\.[0-9]{1,2})?$/,
  // Text: alphanumeric, spaces, and basic punctuation (no HTML/script chars)
  // Blocks: < > & \ ` | and other dangerous characters
  // Split À-ÿ to exclude × (U+00D7) and ÷ (U+00F7)
  text: /^[a-zA-ZÀ-ÖØ-öø-ÿ0-9\s.,!?'"()\-:;]+$/,
};

/**
 * Creates a Zod schema for a required text field
 * Blocks potentially dangerous characters like < > & \ `
 */
export const createTextSchema = (t: (key: string) => string) =>
  z
    .string()
    .min(1, t("validation.required"))
    .max(255, t("validation.max-length-exceeded"))
    .regex(PATTERNS.text, t("validation.invalid-characters"));

/**
 * Creates a Zod schema for a name field (first name, last name)
 * Only allows letters (including accented), spaces, hyphens, apostrophes
 */
export const createNameSchema = (t: (key: string) => string) =>
  z
    .string()
    .trim()
    .min(1, t("validation.name-required"))
    .max(50, t("validation.name-max-length"))
    .regex(PATTERNS.name, t("validation.name-invalid-characters"));

/**
 * Creates a Zod schema for an email field
 * Uses Zod's modern z.email() API (replaces deprecated z.string().email())
 * Uses .pipe() to ensure empty string check happens before email format validation
 */
export const createEmailSchema = (t: (key: string) => string) =>
  z
    .string()
    .min(1, t("validation.email-required"))
    .pipe(
      z
        .email(t("validation.email-invalid"))
        .max(100, t("validation.email-max-length"))
    );

/**
 * Creates a Zod schema for a phone number field
 * US only: requires exactly 10 digits (formatting optional)
 * Allows digits, spaces, hyphens, dots, parentheses only
 */
export const createPhoneSchema = (t: (key: string) => string) =>
  z
    .string()
    .min(1, t("validation.phone-required"))
    .max(14, t("validation.phone-invalid")) // 10 digits + formatting (e.g., "(555) 123-4567")
    .regex(PATTERNS.phone, t("validation.phone-invalid"))
    .refine(
      (value) => {
        // Extract only digits from the phone number
        const digitsOnly = value.replace(/[^0-9]/g, "");
        // US phone: must have exactly 10 digits
        return digitsOnly.length === 10;
      },
      t("validation.phone-invalid"),
    );

/**
 * Creates a Zod schema for a number field
 * Only allows digits
 */
export const createNumberSchema = (t: (key: string) => string) =>
  z
    .string()
    .min(1, t("validation.required"))
    .regex(PATTERNS.number, t("validation.number-invalid"));

/**
 * Creates a Zod schema for height in feet (1-9 feet)
 */
export const createHeightFtSchema = (t: (key: string) => string) =>
  z.preprocess(
    (val) => (val === "" ? undefined : val),
    z.coerce
      .number({ message: t("validation.height-invalid") })
      .int(t("validation.height-invalid"))
      .min(1, t("validation.height-ft-range"))
      .max(9, t("validation.height-ft-range"))
  );

/**
 * Creates a Zod schema for height in inches (0-11 inches)
 */
export const createHeightInSchema = (t: (key: string) => string) =>
  z.preprocess(
    (val) => (val === "" ? undefined : val),
    z.coerce
      .number({ message: t("validation.height-invalid") })
      .int(t("validation.height-invalid"))
      .min(0, t("validation.height-in-range"))
      .max(11, t("validation.height-in-range"))
  );

/**
 * Creates a Zod schema for height in centimeters (50-275 cm)
 */
export const createHeightCmSchema = (t: (key: string) => string) =>
  z.preprocess(
    (val) => {
      if (val === "") return undefined;
      // Reject trailing decimal point (e.g., "170." is invalid)
      if ((val as string).endsWith(".")) return undefined;
      return val;
    },
    z.coerce
      .number({ message: t("validation.height-invalid") })
      .refine((num) => Number(num.toFixed(1)) === num, {
        message: t("validation.height-invalid"),
      })
      .min(50, t("validation.height-cm-range"))
      .max(275, t("validation.height-cm-range")),
  );

/**
 * Creates a Zod schema for weight in pounds (50-700 lbs)
 * Allows decimals with up to 2 decimal places (e.g., "155.5", "155.25")
 */
export const createWeightLbsSchema = (t: (key: string) => string) =>
  z.preprocess(
    (val) => {
      if (val === "") return undefined;
      // Reject trailing decimal point (e.g., "155." is invalid)
      if ((val as string).endsWith(".")) return undefined;
      return val;
    },
    z.coerce
      .number({ message: t("validation.weight-invalid") })
      .refine((num) => Number(num.toFixed(2)) === num, {
        message: t("validation.weight-invalid"),
      })
      .min(50, t("validation.weight-lbs-range"))
      .max(700, t("validation.weight-lbs-range")),
  );

/**
 * Creates a Zod schema for weight in kilograms (20-320 kg)
 * Allows decimals with up to 2 decimal places (e.g., "70.5", "70.25")
 */
export const createWeightKgSchema = (t: (key: string) => string) =>
  z.preprocess(
    (val) => {
      if (val === "") return undefined;
      // Reject trailing decimal point (e.g., "70." is invalid)
      if ((val as string).endsWith(".")) return undefined;
      return val;
    },
    z.coerce
      .number({ message: t("validation.weight-invalid") })
      .refine((num) => Number(num.toFixed(2)) === num, {
        message: t("validation.weight-invalid"),
      })
      .min(20, t("validation.weight-kg-range"))
      .max(320, t("validation.weight-kg-range")),
  );

/**
 * Schema definition with metadata for input behavior.
 * - schemaType: determines which characters are allowed in the input
 *   - 'text': no character filtering (letters, symbols, etc.)
 *   - 'integer': digits only
 *   - 'decimal': digits and decimal point
 */
type SchemaDefinition = {
  creator: (t: (key: string) => string) => z.ZodSchema;
  schemaType: "text" | "integer" | "decimal";
};

/**
 * Lookup table mapping validation types to their schema creators and input metadata.
 * TypeScript will error if a validation type is missing from this object.
 */
const SCHEMA_DEFINITIONS: Record<TextFieldValidationType, SchemaDefinition> = {
  text: { creator: createTextSchema, schemaType: "text" },
  name: { creator: createNameSchema, schemaType: "text" },
  email: { creator: createEmailSchema, schemaType: "text" },
  phone: { creator: createPhoneSchema, schemaType: "text" },
  number: { creator: createNumberSchema, schemaType: "integer" },
  height_ft: { creator: createHeightFtSchema, schemaType: "integer" },
  height_in: { creator: createHeightInSchema, schemaType: "integer" },
  height_cm: { creator: createHeightCmSchema, schemaType: "decimal" },
  weight_lbs: { creator: createWeightLbsSchema, schemaType: "decimal" },
  weight_kg: { creator: createWeightKgSchema, schemaType: "decimal" },
};

/**
 * Validation types that only allow integer input (digits only).
 * Derived from SCHEMA_DEFINITIONS to ensure consistency.
 */
export const INTEGER_VALIDATION_TYPES = new Set(
  Object.entries(SCHEMA_DEFINITIONS)
    .filter(([, definition]) => definition.schemaType === "integer")
    .map(([validationType]) => validationType as TextFieldValidationType),
);

/**
 * Validation types that allow decimal input (digits + decimal point).
 * Derived from SCHEMA_DEFINITIONS to ensure consistency.
 */
export const DECIMAL_VALIDATION_TYPES = new Set(
  Object.entries(SCHEMA_DEFINITIONS)
    .filter(([, definition]) => definition.schemaType === "decimal")
    .map(([validationType]) => validationType as TextFieldValidationType),
);

/**
 * Get the appropriate schema based on validation type
 * Returns a schema that takes a string input and outputs a string
 */
export const getSchemaForType = (
  type: TextFieldValidationType,
  t: (key: string) => string,
) => SCHEMA_DEFINITIONS[type].creator(t);

/**
 * Validate a single field value against its schema
 * Returns null if valid, error message string if invalid
 */
export const validateField = (
  value: string,
  validationType: TextFieldValidationType,
  t: (key: string) => string,
): string | null => {
  // Suppress errors while decimal inputs have a trailing dot
  if (value.endsWith(".") && DECIMAL_VALIDATION_TYPES.has(validationType)) {
    return null;
  }

  const schema = getSchemaForType(validationType, t);

  const result = schema.safeParse(value);

  if (result.success) {
    return null;
  }

  return result.error.issues[0]?.message || t("validation.invalid");
};
